import { useCallback } from "react";
import useAuth from "./useAuth";
import { NavLink } from "react-router-dom";
import { fieldNameToAssetName } from "../components/fields/fieldsHelper";
import { MapPin, Moon, UserCheck, UserX } from "react-feather";
import { dateFromNow, getUrl } from "../utils/staticMethods";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBatteryEmpty,
  faBatteryFull,
  faBatteryHalf,
  faBatteryQuarter,
  faBatteryThreeQuarters,
  faHourglassHalf,
  faPlug,
} from "@fortawesome/free-solid-svg-icons";
import { imageSet } from "../pages/icons/AssetIcon/IconList";
import defaultImg from "../assets/img/icon/GPS/placeholder.png";
import TextFilter from "../components/tableFilters/TextFilter";
import SelectionFilter from "../components/tableFilters/SelectionFilter";
// import api from "../api";
import NumericFilter from "../components/tableFilters/NumericFilter";
import DateFilter from "../components/tableFilters/DateFilter";
import BooleanFilter from "../components/tableFilters/BooleanFilter";

const useCustomFieldsHelper = () => {
  const { user } = useAuth();

  const getColumnsForElementType = (elementType) => {
    if (!user) return [];

    const defaultColumns = (columns) => {
      columns.push({
        Header: "Name",
        accessor: "compoundName",
        type: "link",
        stateId: "id",
        link: `/${elementType}/edit`,
      });
      columns.push({
        Header: "Reference",
        accessor: "reference",
        visibilityName: "reference",
      });
    };

    const customFieldColumns = (columns) => {
      customFieldConfig.customFields.reduce((previousResult, currentValue) => {
        if (currentValue.enabled) {
          var accessor = fieldNameToAssetName(currentValue.fieldName);
          switch (currentValue.fieldName) {
            case "primary_name":
              previousResult.push({
                Header: "Name",
                accessor: "compoundName",
                type: "link",
                stateId: "id",
                link: `/${elementType}/edit`,
              });
              break;
            case "secondary_name":
              break;
            default:
              previousResult.push({
                Header: currentValue.displayName,
                accessor: accessor,
                visibilityName: accessor,
              });
              break;
          }
        }

        return previousResult;
      }, columns);
    };

    const commonColumns = (columns) => {
      columns.unshift({
        Header: "",
        accessor: "icon",
        type: "icon",
        disableSortBy: true,
      });

      if (elementType === "assets" || elementType === "people") {
        columns.push({
          Header: "Last Seen",
          accessor: "lastSeen",
          visibilityName: "last-seen",
          Cell: (cell) => {
            if (cell.value && cell.value.indexOf("0001-01-01") === -1) {
              return (
                <>
                  <MapPin width={14} height={14} className="me-1" />
                  {dateFromNow(cell.value)}
                </>
              );
            }
            return (
              <span>
                <Moon width={14} height={14} className="me-1" /> Waiting...
              </span>
            );
          },
        });
      }
      if (elementType === "assets") {
        columns.push({
          Header: "Asset Battery",
          accessor: "lastPosition.supply",
          visibilityName: "supply",
          Cell: (cell) => {
            let supply = cell.row?.original?.lastPosition?.supply;

            if (typeof supply === "number") {
              let level = "";

              if (supply === 0 || supply === 500) {
                level = "Disconnected";
              } else if (supply === 1) {
                level = "Connected";
              } else if (supply > 12250) {
                level = "High";
              } else if (supply > 11500 && supply <= 12250) {
                level = "Medium";
              } else if (supply > 0 && supply <= 11500) {
                level = "Low";
              } else {
                level = "Waiting for data";
              }

              return (
                <>
                  {level === "High" && (
                    <FontAwesomeIcon
                      icon={faBatteryFull}
                      className="srl-green"
                    />
                  )}
                  {level === "Medium" && (
                    <FontAwesomeIcon
                      icon={faBatteryHalf}
                      className="srl-canary"
                    />
                  )}
                  {level === "Low" && (
                    <FontAwesomeIcon
                      icon={faBatteryQuarter}
                      className="srl-red"
                    />
                  )}
                  {level === "Connected" && (
                    <FontAwesomeIcon icon={faPlug} className="grey-500" />
                  )}
                  {level === "Disconnected" && (
                    <FontAwesomeIcon icon={faPlug} className="grey-500" />
                  )}
                  {level === "Waiting for data" && (
                    <FontAwesomeIcon
                      icon={faHourglassHalf}
                      className="grey-500"
                    />
                  )}
                  <span>&nbsp;{level}</span>
                  {["Low", "Medium", "High"].includes(level) && supply && (
                    <span>&nbsp;({supply} mV)</span>
                  )}
                </>
              );
            } else {
              return <></>;
            }
          },
          // disableSortBy: true,
        });
        columns.push({
          Header: "Device Battery",
          accessor: "lastPosition.battery",
          visibilityName: "battery",
          Cell: (cell) => {
            let battery = cell.row?.original?.lastPosition?.battery;

            if (typeof battery === "number") {
              const getBatteryIcon = (battery) => {
                if (battery >= 10 && battery < 25) {
                  return faBatteryQuarter;
                } else if (battery < 10) {
                  return faBatteryEmpty;
                } else if (battery >= 25 && battery <= 50) {
                  return faBatteryHalf;
                } else if (battery > 50 && battery <= 75) {
                  return faBatteryThreeQuarters;
                } else {
                  return faBatteryFull;
                }
              };

              return (
                <>
                  <FontAwesomeIcon
                    icon={getBatteryIcon(battery)}
                    fixedWidth
                    className="me-1"
                  />{" "}
                  {`${battery} %`}
                </>
              );
            } else {
              return <></>;
            }
          },
          // disableSortBy: true,
        });
      }

      if (elementType === "zones") {
        columns.push({
          Header: "Contents",
          accessor: "contentsCount",
          visibilityName: "contents-count",
          disableSortBy: true,
        });
      }

      if (user?.role === "Root") {
        columns.push({
          Header: "Customer",
          accessor: "customerName",
          show: user?.role === "Root",
          disableSortBy: true,
          Cell: (cell) => (
            <NavLink
              to={{
                pathname: `/customers/edit`,
                search: `?id=${cell.row?.original?.customerId}`,
              }}
            >
              {cell.value}
            </NavLink>
          ),
        });
      }

      columns.push({
        Header: "Hashtags",
        accessor: "hashTags",
        visibilityName: "hash-tags",
        disableSortBy: true,
        Cell: (cell) => (
          <div>
            {cell.value &&
              cell.value.map((tag, i) => (
                <NavLink
                  key={tag}
                  to={{
                    pathname: "/globalSearch",
                    search: `?searchString=${encodeURIComponent("#" + tag)}`,
                  }}
                >
                  <span
                    key={i}
                    className="badge bg-primary me-1 my-1 cursor-pointer"
                  >
                    {tag}
                  </span>
                </NavLink>
              ))}
          </div>
        ),
      });
    };

    let customFieldConfig = null;
    let columns = [];

    if (user.role !== "Root") {
      switch (elementType) {
        case "assets":
          customFieldConfig = user.assetFieldConfig;
          break;
        case "people":
          customFieldConfig = user.personFieldConfig;
          break;
        case "zones":
          customFieldConfig = user.zoneFieldConfig;
          break;
        default:
          break;
      }
    }

    if (customFieldConfig && customFieldConfig.enabled) {
      customFieldColumns(columns);
    } else {
      defaultColumns(columns);
    }

    commonColumns(columns);

    return columns;
  };

  // const getColumnsForElementPeopleRoleUser = () => {
  //   if (!user) return [];

  //   let customFieldConfig = null;
  //   let columns = [];

  //   if (customFieldConfig && customFieldConfig.enabled) {
  //     customFieldColumns(columns);
  //   } else {
  //     defaultColumns(columns);
  //   }

  //   // commonColumns(columns);

  //   return columns;
  // };

  const renderHeader = useCallback(({ column }) => {
    if (column.filterValue) {
      return <div className="w-100 badge-soft-secondary">{column.name}</div>;
    } else {
      return <>{column.name}</>;
    }
  }, []);

  // const fetchFilterOptions = useCallback(async (columnName) => {
  //   try {
  //     var result = await api.get(
  //       `usersessions/filteroptions?columnname=${columnName}`
  //     );
  //     return result.data
  //       .filter((v) => {
  //         return v !== null;
  //       })
  //       .map((v) => {
  //         return { label: v, value: v, checked: false };
  //       });
  //   } catch (error) {
  //     return [];
  //   }
  // }, []);

  const getColumnsForElementPeopleRoleUser = useCallback(() => {
    if (!user) return [];

    const columns = [];
    let customFieldConfig = null;

    if (user.role === "Admin") {
      customFieldConfig = user.personFieldConfig;
    }

    if (customFieldConfig && customFieldConfig.enabled) {
      customFieldConfig.customFields.reduce((previousResult, currentValue) => {
        if (currentValue.enabled) {
          var accessor = currentValue.fieldName;
          switch (currentValue.fieldName) {
            case "primary_name":
            case "secondary_name":
              previousResult.push({
                Header: renderHeader,
                name: currentValue.displayName,
                accessor: accessor,
                disableSortBy: false,
                // disableFilters: true,
                Filter: TextFilter,
                Cell: ({ column, row, cell, value }) => {
                  return (
                    <>
                      <NavLink
                        to={{
                          pathname: "/users/edit",
                          search: `?id=${row.original["_id"]}`,
                        }}
                      >
                        {cell.value}
                      </NavLink>
                    </>
                  );
                },
              });
              break;

            default:
              previousResult.push({
                Header: renderHeader,
                name: currentValue.displayName,
                accessor: accessor,
                // minWidth: 300,
                // width: 200,
                disableSortBy: false,
                disableFilters: false,
                Filter: TextFilter,
                Cell: ({ column, row, cell, value }) => {
                  return <>{cell.value}</>;
                },
              });
              break;
          }
        }

        return previousResult;
      }, columns);
    } else {
      columns.push({
        Header: renderHeader,
        name: "Name",
        accessor: "compound_name",
        disableSortBy: false,
        // disableFilters: true,
        Filter: TextFilter,
        Cell: ({ column, row, cell, value }) => {
          return (
            <>
              <NavLink
                to={{
                  pathname: "/users/edit",
                  search: `?id=${row.original["_id"]}`,
                }}
              >
                {cell.value}
              </NavLink>
            </>
          );
        },
      });

      columns.push({
        Header: renderHeader,
        name: "Reference",
        accessor: "reference",
        disableSortBy: false,
        disableFilters: false,
        Filter: TextFilter,
        Cell: ({ column, row, cell, value }) => {
          return <>{cell.value}</>;
        },
      });
    }

    columns.unshift({
      Header: renderHeader,
      name: "Icon",
      accessor: "icon",
      width: 50,
      disableSortBy: true,
      disableFilters: true,
      Cell: ({ column, row, cell, value }) => {
        return (
          <>
            <img
              className="table-icon"
              alt="table-icon"
              src={
                imageSet[getUrl(cell.value)]
                  ? require("./../assets/img/icon/" +
                      imageSet[getUrl(cell.value)]).default
                  : defaultImg
              }
            />
          </>
        );
      },
    });

    columns.push({
      Header: renderHeader,
      name: "Role",
      accessor: "role",
      disableFilters: false,
      Filter: SelectionFilter,
      filterOptions: [
        { label: "Admin", value: "Admin" },
        { label: "User", value: "User" },
      ],
      Cell: ({ column, row, cell, value }) => {
        return <>{cell.value}</>;
      },
    });

    columns.push({
      Header: renderHeader,
      name: "Email",
      accessor: "email",
      Filter: TextFilter,
    });

    columns.push({
      Header: renderHeader,
      name: "Profile Name",
      accessor: "permission.permission_name",

      Cell: ({ column, row, cell, value }) => {
        return (
          <>
            {row.original.role === "Admin" ? (
              <div>
                <i>(Administrator)</i>
              </div>
            ) : (
              <NavLink
                to={{
                  pathname: "/accessprofiles/edit",
                  search: `?id=${row.original["permission_id"]}`,
                }}
              >
                {cell.value}
              </NavLink>
            )}
          </>
        );
      },
      //canFilter: false,
      // disableFilters: true,
      Filter: TextFilter,
    });

    const activeUser = <UserCheck color="green" strokeWidth={"2px"} />;
    const inactiveUser = (
      <UserX color="red" strokeWidth={"2px"} opacity={"0.4"} />
    );

    columns.push({
      Header: renderHeader,
      name: "Active",
      accessor: "is_active",
      Filter: BooleanFilter,
      filterOptions: [
        { label: activeUser, value: "═╬>1", title: "Show only active users" },
        {
          label: inactiveUser,
          value: "═╬>0",
          title: "Show only inactive users",
        },
      ],
      Cell: ({ column, row, cell, value }) => {
        return <>{cell.value ? activeUser : inactiveUser}</>;
      },
    });

    columns.push({
      Header: renderHeader,
      name: "Last Seen",
      accessor: "user_session.last_seen",
      Filter: DateFilter,
      Cell: ({ column, row, cell, value }) => {
        return <>{dateFromNow(cell.value)}</>;
      },
    });

    columns.push({
      Header: renderHeader,
      name: "Current Month Usage",
      accessor: "current_month.total_session_seconds",
      Filter: NumericFilter,
      Cell: ({ column, row, cell, value }) => {
        return <>{cell.value ? Math.ceil(cell.value / 60) : 0}</>;
      },
    });

    columns.push({
      Header: renderHeader,
      name: "Current Month Sessions",
      accessor: "current_month.total_sessions",
      Filter: NumericFilter,
    });

    columns.push({
      Header: renderHeader,
      name: "Total Usage",
      accessor: "user_session.total_session_time",
      Filter: NumericFilter,
      Cell: ({ column, row, cell, value }) => {
        return <>{cell.value ? Math.ceil(cell.value / 60) : 0}</>;
      },
    });

    columns.push({
      Header: renderHeader,
      name: "Total Sessions",
      accessor: "user_session.total_sessions",
      Filter: NumericFilter,
    });

    columns.push({
      Header: renderHeader,
      name: "Average Session Length",
      accessor: "user_session.lifetime_average_session_length",
      Filter: NumericFilter,
      Cell: ({ column, row, cell, value }) => {
        return <>{cell.value ? Math.ceil(cell.value / 60) : 0}</>;
      },
    });

    return columns;
  }, []);

  return {
    getColumnsForElementType,
    getColumnsForElementPeopleRoleUser,
  };
};

export default useCustomFieldsHelper;
